import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button
} from '@mui/material';
import {
  People as PeopleIcon,
  VpnKey as RolesIcon,
  Hotel as HotelIcon,
  EventAvailable as AvailabilityIcon,
  Bookmark as BookingIcon,
  LocalOffer as CouponIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Festival as FestivalIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { authService, userService, bookingService, hotelService, couponService } from '../../services';
import UserManagement from '../UserManagement';
import RoleManagement from '../RoleManagement';
import HotelAvailabilityChecker from '../HotelAvailabilityChecker';
import BookingManagement from '../BookingManagement';
import CouponManagement from '../CouponManagement';
import HotelManagement from '../HotelManagement';
import FestivalManagement from '../FestivalManagement';

// TabPanel component to handle tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
      style={{ padding: '24px 0' }}
    >
      {value === index && children}
    </div>
  );
}

const AdminDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    bookings: 0,
    hotels: 0,
    coupons: 0
  });
  const [error, setError] = useState(null);
  
  // Check if user is admin
  const isAdmin = authService.hasRole('admin');
  const isManager = authService.hasRole('manager');
  const hasAccess = isAdmin || isManager;
  
  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }
    
    fetchDashboardStats();
  }, [hasAccess]);
  
  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch counts in parallel
      const [users, bookings, hotels, coupons] = await Promise.all([
        userService.getAllUsers(),
        bookingService.fetchAllBookings(),
        hotelService.fetchAllHotels(),
        couponService.fetchAllCoupons()
      ]);
      
      setStats({
        users: users.length,
        bookings: bookings.length,
        hotels: hotels.length,
        coupons: coupons.length
      });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // If not admin or manager, show access denied
  if (!hasAccess) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied. You must be an admin or manager to access the admin dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Dashboard Overview */}
        {tabValue === 0 && (
          <Box sx={{ mt: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <PeopleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h5" component="div">
                        {stats.users}
                      </Typography>
                      <Typography color="text.secondary">
                        Users
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => setTabValue(1)}>Manage Users</Button>
                    </CardActions>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <BookingIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h5" component="div">
                        {stats.bookings}
                      </Typography>
                      <Typography color="text.secondary">
                        Bookings
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => setTabValue(4)}>View Bookings</Button>
                    </CardActions>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <HotelIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h5" component="div">
                        {stats.hotels}
                      </Typography>
                      <Typography color="text.secondary">
                        Hotels
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => setTabValue(3)}>Manage Hotels</Button>
                    </CardActions>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <CouponIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h5" component="div">
                        {stats.coupons}
                      </Typography>
                      <Typography color="text.secondary">
                        Active Coupons
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => setTabValue(5)}>Manage Coupons</Button>
                    </CardActions>
                  </Card>
                </Grid>
                
                {/* Additional dashboard widgets can be added here */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Quick Actions
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                      <Button variant="outlined" startIcon={<PeopleIcon />} onClick={() => setTabValue(1)}>
                        Manage Users
                      </Button>
                      <Button variant="outlined" startIcon={<RolesIcon />} onClick={() => setTabValue(2)}>
                        Manage Roles
                      </Button>
                      <Button variant="outlined" startIcon={<AvailabilityIcon />} onClick={() => setTabValue(6)}>
                        Room Availability
                      </Button>
                      <Button variant="outlined" startIcon={<CouponIcon />} onClick={() => setTabValue(5)}>
                        Manage Coupons
                      </Button>
                      <Button 
                        variant="outlined" 
                        startIcon={<PersonIcon />} 
                        component={RouterLink} 
                        to="/profile"
                      >
                        My Profile
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Tabs for different management sections */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="admin dashboard tabs"
        >
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<PeopleIcon />} label="Users" />
          <Tab icon={<RolesIcon />} label="Roles" />
          <Tab icon={<HotelIcon />} label="Hotels" />
          <Tab icon={<BookingIcon />} label="Bookings" />
          <Tab icon={<CouponIcon />} label="Coupons" />
          <Tab icon={<AvailabilityIcon />} label="Availability" />
          <Tab icon={<FestivalIcon />} label="Festivals" />
        </Tabs>
        
        {/* Tab content */}
        <TabPanel value={tabValue} index={0}>
          {/* Dashboard content is shown above */}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <UserManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <RoleManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <HotelManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={4}>
          <BookingManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={5}>
          <CouponManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={6}>
          <HotelAvailabilityChecker />
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <FestivalManagement />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AdminDashboard;
